import { Fragment, useMemo } from 'react'
import Accordion from 'LuxKit/Accordion'
import Divider from 'LuxKit/Divider'
import Group from 'components/utils/Group'
import {
  ACTION_REWARD_GROUP_CONFIGS,
  type SupportedLoyaltyActionReward,
} from 'loyalty/constants/loyaltyActionRewards'
import { sortBy, sortByAll, type SortField } from 'lib/array/arrayUtils'
import { objectKeys } from 'lib/object/objectUtils'
import getLoyaltyActionRewardsSummary from 'loyalty/lib/getLoyaltyActionRewardsSummary'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { useIntl } from 'react-intl'
import LoyaltyActionRewardsListItem from './LoyaltyActionRewardsListItem'

const ACTION_REWARD_GROUPS_IN_ORDER = sortBy(
  objectKeys(ACTION_REWARD_GROUP_CONFIGS),
  (groupType) => ACTION_REWARD_GROUP_CONFIGS[groupType].order,
  'asc',
)

const ACTION_REWARD_SORT_FIELDS: Array<SortField<SupportedLoyaltyActionReward>> = [
  { selector: (reward) => reward.isComplete, direction: 'asc' },
  { selector: (reward) => reward.earnEstimate?.bonusPoints ?? 0, direction: 'desc' },
]

interface Props {
  rewards?: Array<SupportedLoyaltyActionReward>
  tier: App.LoyaltyTier
}

function LoyaltyActionRewardsListGrouped({ rewards = [], tier }: Props) {
  const intl = useIntl()

  const groups = useMemo(
    () => ACTION_REWARD_GROUPS_IN_ORDER.flatMap((groupType) => {
      const groupRewards = rewards.filter((reward) => reward.groupType === groupType)
      const sortedGroupRewards = sortByAll(groupRewards, ACTION_REWARD_SORT_FIELDS)

      if (sortedGroupRewards.length === 0) {
        return []
      }

      return [{
        groupType,
        title: ACTION_REWARD_GROUP_CONFIGS[groupType].title,
        rewards: sortedGroupRewards,
      }]
    }),
    [rewards],
  )
  const defaultOpenGroupIndex = groups.findIndex((group) => group.rewards.some((reward) => !reward.isComplete))

  return (
    <Group direction="vertical" gap={8}>
      {groups.map((group, sectionIndex) => {
        const { completedCount: completed, totalCount: total } = getLoyaltyActionRewardsSummary(group.rewards)
        const title = intl.formatMessage(group.title, { luxPlusProgramName: LUXURY_PLUS.PROGRAM_NAME })
        const progressLabel = intl.formatMessage(
          {
            id: 'uwGHRAHmKIuqbbVx9BN7',
            defaultMessage: '{completed}/{total} completed',
            description:
              'Société Quests grouped section progress under each action-reward group heading',
          },
          { completed, total },
        )

        return (
          <Fragment key={group.groupType}>
            {sectionIndex > 0 && <Divider kind="primary" />}
            <Accordion
              heading={title}
              subtitle={progressLabel}
              defaultOpen={sectionIndex === defaultOpenGroupIndex}
              hideDivider
            >
              {group.rewards.map((reward, index) => (
                <Fragment key={reward.id}>
                  {index !== 0 && <Divider kind="secondary" />}
                  <LoyaltyActionRewardsListItem
                    reward={reward}
                    tier={tier}
                    itemPosition={index + 1}
                  />
                </Fragment>
              ))}
            </Accordion>
          </Fragment>
        )
      })}
    </Group>
  )
}

export default LoyaltyActionRewardsListGrouped
